"use client";

import { ArrowRight } from "lucide-react";
import { RuntimeImage, RuntimeLink, useLocale } from "../../../runtime/RendererRuntime";
import { t } from "../../../i18n/strings";
import type { ImageFieldValue, LinkTarget } from "../../../types";
import { resolveLinkHref } from "../../../utils/links";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, resolveImageValue } from "../../../utils/imageOptions";

type Props = {
  title: string;
  excerpt: string;
  image: ImageFieldValue;
  href: LinkTarget;
};

/** Compact news card with left image and navigation arrow. */
export function NewsItemCard({ title, excerpt, image, href }: Props) {
  const locale = useLocale();
  const safeHref = resolveLinkHref(href) ?? "/";
  const { src, options } = resolveImageValue(image);
  const imgSrc = src || "/media/welcome_background.jpg";
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  return (
    <RuntimeLink
      href={safeHref}
      aria-label={`${t(locale, "readArticlePrefix")}${title}`}
      className="block md:h-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0095DB]"
    >
      {/* Mobile : 156 px (au lieu de 172, revue client 16/08 : cartes trop hautes) ; le titre
          garde ses 4 lignes (18 px x 1,275 x 4 = 92 px) + marges 24 + espace 8 + flèche 28 = 152. */}
      <article className="overflow-hidden shadow-sm h-[156px] max-h-[156px] md:h-full md:max-h-none md:min-h-[200px] rounded-sm">
        <div className="grid grid-cols-[minmax(0,_min(240px,_33.333%))_minmax(0,_1fr)] md:grid-cols-[minmax(0,_min(340px,_45%))_minmax(0,_1fr)] gap-0 md:h-full">
          <div
            className={mergeClassNames(
              "relative w-full bg-slate-200 h-[156px] max-h-[156px] md:h-full md:max-h-none overflow-hidden",
              overrideProps.className
            )}
            style={overrideProps.style}
          >
            <RuntimeImage
              src={imgSrc}
              alt={title}
              className="absolute inset-0 h-full w-full object-cover object-center"
              style={objectPositionStyle}
              loading="lazy"
            />
          </div>
          <div className="min-w-0 p-3 sm:p-4 bg-[#EDF7FC] h-[156px] max-h-[156px] md:h-full md:max-h-none flex flex-col items-start text-left gap-2 md:gap-3">
            {/* Tablette : titre en 18px (au lieu de 20) et une ligne de plus (6) ;
                le desktop (lg+) revient à 20px / 5 lignes. */}
            <h4
              className="text-[#005CA9] text-[18px] xl:text-[20px] font-bold leading-[127.5%] line-clamp-4 md:line-clamp-6 lg:line-clamp-5 lg:text-[20px]"
              style={{ fontFamily: "Nunito Sans" }}
            >
              {title}
            </h4>
            <div className="w-full flex-1 flex gap-3 min-h-0">
              <div className="flex-1 min-w-0" />
              <div className="flex flex-col justify-end shrink-0">
                <span className="inline-flex items-center text-sm xl:text-base md:text-base font-semibold text-[#0095DB]">
                  <span className="inline-flex h-7 w-7 md:h-8 md:w-8 items-center justify-center rounded-full border-2 border-[#0095DB]">
                    <ArrowRight className="h-3.5 w-3.5 md:h-4 md:w-4 stroke-[3]" />
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </article>
    </RuntimeLink>
  );
}



